import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { MapPin, CalendarClock } from "lucide-react";
import axios from "axios";
import { API } from "../lib/api";
import CountUp from "./CountUp";

const DEFAULTS = {
    headline: "Umwelttechnik mit echter Baustellenerfahrung.",
    paragraphs: [
        "Die Möbus Umweltschutz GmbH ist ein Familienunternehmen mit über 50 Jahren Erfahrung am Markt. An unserem Standort in Neustadt in Sachsen verbinden wir langjährige Erfahrung mit moderner Umwelttechnik, praktischer Baustellenerfahrung und kurzen Entscheidungswegen.",
        "Wir stehen für Umwelttechnik, Wasserbehandlung, Baustellenlösungen und technische Anlagen. Unsere Projekte führen uns durch ganz Deutschland und darüber hinaus – von klassischen Baustellen bis hin zu großen Infrastrukturprojekten wie Tunnelbau, Brückenbau, Pumpspeicherkraftwerken und industriellen Anlagen.",
        "Dabei sind wir kein Konzern mit langen Wegen, sondern ein praxisnahes Team, das anpackt, Lösungen findet und moderne Technik mit echter Baustellenerfahrung verbindet.",
    ],
    stat1_number: "50",
    stat1_suffix: "+ Jahre",
    stat1_label: "Familienbetrieb mit Erfahrung",
    stat2_title: "01844 Neustadt",
    stat2_label: "Sachsen · Deutschlandweit im Einsatz",
};

export function Stat1Value({ number, suffix }) {
    const n = parseInt(number, 10);
    const suf = suffix || "";
    return (
        <>
            {!isNaN(n) ? <CountUp to={n} duration={2} /> : number}
            {suf.startsWith("+") ? (
                <>
                    <span className="text-moebus-green">+</span>
                    {suf.slice(1)}
                </>
            ) : (
                suf
            )}
        </>
    );
}

export default function AboutUs() {
    const [c, setC] = useState(DEFAULTS);

    useEffect(() => {
        axios
            .get(`${API}/content/about`)
            .then((r) => {
                if (r.data?.headline) setC(r.data);
            })
            .catch(() => {});
    }, []);

    return (
        <section
            id="ueber-uns"
            data-testid="about-section"
            className="py-20 md:py-28 bg-white relative overflow-hidden"
        >
            <div className="container mx-auto px-4 md:px-8">
                <div className="max-w-4xl mx-auto">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-100px" }}
                        transition={{ duration: 0.8 }}
                    >
                        <div className="text-xs tracking-[0.3em] uppercase text-moebus-green font-semibold mb-4">
                            Wer wir sind
                        </div>
                        <h2 className="font-heading font-bold text-3xl sm:text-4xl lg:text-5xl text-moebus-deep leading-tight text-balance">
                            {c.headline}
                        </h2>
                        <div className="mt-8 space-y-5 text-base md:text-lg text-slate-600 leading-relaxed">
                            {(c.paragraphs || []).map((p, i) => (
                                <p key={i}>{p}</p>
                            ))}
                        </div>
                    </motion.div>

                    {/* Stats row */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-100px" }}
                        transition={{ duration: 0.7, delay: 0.2 }}
                        className="mt-12 md:mt-16 grid sm:grid-cols-2 gap-4 md:gap-6"
                    >
                        <div className="flex items-start gap-4 p-5 md:p-6 rounded-lg border border-moebus-border bg-moebus-surface">
                            <div className="w-11 h-11 rounded-md bg-emerald-50 text-moebus-green flex items-center justify-center flex-shrink-0">
                                <CalendarClock size={22} strokeWidth={1.8} />
                            </div>
                            <div>
                                <div className="font-heading font-bold text-2xl md:text-3xl text-moebus-deep">
                                    <Stat1Value number={c.stat1_number} suffix={c.stat1_suffix} />
                                </div>
                                <div className="text-xs md:text-sm text-slate-500 mt-1">{c.stat1_label}</div>
                            </div>
                        </div>

                        <div className="flex items-start gap-4 p-5 md:p-6 rounded-lg border border-moebus-border bg-moebus-surface">
                            <div className="w-11 h-11 rounded-md bg-blue-50 text-moebus-blue flex items-center justify-center flex-shrink-0">
                                <MapPin size={22} strokeWidth={1.8} />
                            </div>
                            <div>
                                <div className="font-heading font-bold text-2xl md:text-3xl text-moebus-deep">
                                    {c.stat2_title}
                                </div>
                                <div className="text-xs md:text-sm text-slate-500 mt-1">{c.stat2_label}</div>
                            </div>
                        </div>
                    </motion.div>
                </div>
            </div>
        </section>
    );
}
